"use client";

import { useState } from "react";
import { Mail, Sparkles, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import Link from "next/link";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const res = await fetch("/api/auth/forgot-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || "Ocorreu um erro inesperado.");
            }

            setSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erro ao processar solicitação.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background elements (similar to login) */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full pointer-events-none" />

            <div className="w-full max-w-md relative z-10 animate-in fade-in zoom-in-95 duration-500">
                {/* Logo & Branding */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand/15 border border-brand/20 mb-3 shadow-lg shadow-brand/10 overflow-hidden">
                        <img src="/images/avatar_mike_ia_sf.png" alt="MikeIA" className="w-10 h-10 object-contain" />
                    </div>
                    <h1 className="text-2xl font-bold text-content tracking-tight">
                        Mike
                        <span className="text-brand">IA</span>
                    </h1>
                    <p className="text-content-muted mt-1.5 text-sm">
                        Recuperação de Senha
                    </p>
                </div>

                {/* Card */}
                <div className="bg-surface/80 backdrop-blur-xl border border-divider rounded-2xl p-6 shadow-2xl shadow-black/20">
                    
                    {success ? (
                        <div className="text-center space-y-4 py-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500 mb-2">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <h2 className="text-xl font-bold text-content">E-mail Enviado!</h2>
                            <p className="text-sm text-content-muted">
                                Se o e-mail <strong>{email}</strong> estiver cadastrado em nossa base, você receberá um link para redefinir sua senha em instantes.
                            </p>
                            <div className="pt-4">
                                <Link 
                                    href="/login"
                                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 bg-background hover:bg-surface-hover border border-divider text-content font-semibold rounded-xl transition-all text-sm"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Voltar para o Login
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles className="w-3.5 h-3.5 text-brand" />
                                <h2 className="text-sm font-semibold text-content">
                                    Informe seu E-mail
                                </h2>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Email */}
                                <div className="space-y-1.5">
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-medium text-content-muted"
                                    >
                                        E-mail de Cadastro
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-muted" />
                                        <input
                                            id="email"
                                            type="email"
                                            required
                                            autoComplete="email"
                                            placeholder="seu@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 bg-background/60 border border-divider rounded-xl text-sm text-content placeholder:text-content-muted/50 transition-all duration-200 focus:border-brand focus:ring-1 focus:ring-brand/40 hover:border-dark-text-muted/40"
                                        />
                                    </div>
                                </div>

                                {/* Error message */}
                                {error && (
                                    <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-lg px-4 py-2.5">
                                        <span className="shrink-0">⚠️</span>
                                        <span>{error}</span>
                                    </div>
                                )}

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={isLoading || !email}
                                    className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-brand/25 hover:shadow-brand/40 hover:scale-[1.01] active:scale-[0.99] cursor-pointer flex items-center justify-center gap-2 text-sm"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        "Solicitar Recuperação"
                                    )}
                                </button>
                                
                                <div className="pt-2">
                                    <Link 
                                        href="/login"
                                        className="w-full inline-flex items-center justify-center gap-2 py-2 text-content-muted hover:text-content transition-colors text-sm"
                                    >
                                        <ArrowLeft className="w-3.5 h-3.5" />
                                        Lembrei minha senha
                                    </Link>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
